import React from 'react';
import L from 'leaflet';
import { STATUS_COLORS } from '../utils/mapIcons';

/**
 * @file MapPopups.jsx
 * @description Components for individual project popups and multi-project cluster popups.
 */

/**
 * Content for a single project popup.
 * 
 * @param {Object} props
 * @param {Object} props.project - The project data
 * @param {Function} props.onProjectSelect - Detail callback
 * @param {Function} props.onProjectEdit - Edit callback
 */
export const ProjectPopupContent = ({ project, onProjectSelect, onProjectEdit }) => {
    const statusLabel = project.status === 'active' ? 'Aktiv' :
        project.status === 'paused' ? 'Inaktiv' :
            project.status === 'completed' ? 'Abgeschlossen' : 'Archiv';
    const statusColor = STATUS_COLORS[project.status] || STATUS_COLORS.active;

    return (
        <div className="map-popup-content" style={{ minWidth: '220px', padding: '5px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: '#374151',
                    background: '#f3f4f6',
                    padding: '4px 10px',
                    borderRadius: '6px'
                }}>
                    {project.project_number}
                </span>
                <span style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: 'white',
                    backgroundColor: statusColor,
                    padding: '3px 8px',
                    borderRadius: '6px',
                    textTransform: 'capitalize'
                }}>
                    {statusLabel}
                </span>
            </div>

            <h4 style={{ margin: '0 0 4px 0', fontSize: '1.1rem', color: '#111827' }}>{project.name}</h4>
            <p style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: '#6b7280', lineHeight: '1.4' }}>{project.address}</p>

            <div style={{ display: 'flex', gap: '10px' }}>
                {onProjectSelect && (
                    <button
                        onClick={() => onProjectSelect(project)}
                        style={{
                            flex: 1,
                            padding: '8px 12px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            background: '#4f46e5',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        Details
                    </button>
                )}
                {onProjectEdit && (
                    <button
                        onClick={() => onProjectEdit(project)}
                        style={{
                            flex: 1,
                            padding: '8px 12px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            background: 'white',
                            color: '#374151',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        Bearbeiten
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Creates and opens a multi-project popup for a cluster.
 * 
 * @param {L.Map} map - Leaflet map instance
 * @param {L.LatLng} latlng - Position to open popup at
 * @param {Array} projects - List of project data objects in the cluster
 * @param {Function} onProjectSelect - Callback for details
 * @param {Function} onProjectEdit - Callback for edit
 */
export const openMultiProjectPopup = (map, latlng, projects, onProjectSelect, onProjectEdit) => {
    if (projects.length === 0) return;

    const popupContent = L.DomUtil.create('div', 'multi-project-popup');
    popupContent.style.width = '200px';
    popupContent.style.maxHeight = '300px';
    popupContent.style.overflowY = 'auto';
    popupContent.style.paddingRight = '5px';

    projects.forEach(project => {
        const statusLabel = project.status === 'active' ? 'Aktiv' :
            project.status === 'paused' ? 'Inaktiv' :
                project.status === 'completed' ? 'Abgeschlossen' : 'Archiv';
        const statusColor = STATUS_COLORS[project.status] || STATUS_COLORS.active;

        const container = L.DomUtil.create('div', '', popupContent);
        container.style.marginBottom = '15px';
        container.style.paddingBottom = '15px';
        container.style.borderBottom = '1px solid #eee';

        const header = L.DomUtil.create('div', '', container);
        header.style.display = 'flex';
        header.style.justifyContent = 'space-between';
        header.style.alignItems = 'center';
        header.style.marginBottom = '10px';
        header.innerHTML = `
            <span style="font-size:0.8rem; font-weight:bold; color:#374151; background:#f3f4f6; padding:3px 8px; border-radius:6px">${project.project_number}</span>
            <span style="font-size:0.7rem; font-weight:600; color:white; background-color:${statusColor}; padding:2px 7px; border-radius:5px">${statusLabel}</span>
        `;

        const title = L.DomUtil.create('h4', '', container);
        title.style.margin = '0 0 4px 0';
        title.style.fontSize = '1rem';
        title.innerText = project.name;

        const addr = L.DomUtil.create('p', '', container);
        addr.style.margin = '0 0 12px 0';
        addr.style.fontSize = '0.8rem';
        addr.style.color = '#6b7280';
        addr.innerText = project.address;

        const actions = L.DomUtil.create('div', '', container);
        actions.style.display = 'flex';
        actions.style.gap = '8px';

        const detailsBtn = L.DomUtil.create('button', '', actions);
        detailsBtn.innerText = 'Details';
        detailsBtn.style.cssText = 'flex:1; padding:7px 10px; font-size:0.85rem; font-weight:600; background:#4f46e5; color:white; border:none; border-radius:8px; cursor:pointer;';
        detailsBtn.onclick = () => {
            onProjectSelect(project);
            map.closePopup();
        };

        const editBtn = L.DomUtil.create('button', '', actions);
        editBtn.innerText = 'Bearbeiten';
        editBtn.style.cssText = 'flex:1; padding:7px 10px; font-size:0.85rem; font-weight:600; background:white; color:#374151; border:1px solid #d1d5db; border-radius:8px; cursor:pointer;';
        editBtn.onclick = () => {
            onProjectEdit(project);
            map.closePopup();
        };
    });

    L.popup({
        maxWidth: 250,
        className: 'custom-multi-popup'
    })
        .setLatLng(latlng)
        .setContent(popupContent)
        .openOn(map);
};
